import React, { useState, useEffect } from "react";
import Pagination from "@/components/pagination";
import SearchInput from "@/components/search-input";
import API from "@/services/api";
import toast from "react-hot-toast";
import UserModal from "../components/UserModal";
import OrganizationModal from "../components/OrganizationModal";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    total: "Total",
    no_users: "No users found",
    table: {
      name: "Name",
      email: "Email",
      role: "Role",
      organization: "Organization",
      created_date: "Created Date",
      last_login: "Last Login",
      actions: "Actions",
    },
    roles: {
      admin: "Admin",
      recruiter: "Recruiter",
      candidate: "Candidate",
      agency: "Agency",
      na: "N/A",
    },
    buttons: {
      user: "User",
      org: "Org",
    },
  },
  fr: {
    total: "Total",
    no_users: "Aucun utilisateur trouvé",
    table: {
      name: "Nom",
      email: "Email",
      role: "Rôle",
      organization: "Organisation",
      created_date: "Date de création",
      last_login: "Dernière connexion",
      actions: "Actions",
    },
    roles: {
      admin: "Admin",
      recruiter: "Recruteur",
      candidate: "Candidat",
      agency: "Agence",
      na: "N/A",
    },
    buttons: {
      user: "Utilisateur",
      org: "Org",
    },
  },
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const limit = 25;
  const { t } = useComponentTranslation(translations);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await API.post("/user/search", {
        page: currentPage,
        limit: limit,
        role_candidate: false,
        ...(searchTerm && { search: searchTerm }),
      });

      if (!response.ok) throw response;
      setUsers(response.data || []);
      setTotal(response.total || 0);
    } catch (e) {
      console.error("Error fetching users:", e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
  };

  const handleViewOrganization = async (user) => {
    const orgId = user.organisations?.[0]?.id;
    if (!orgId) {
      toast.error("No organisation");
      return;
    }

    try {
      const response = await API.get(`/organization/${orgId}`);
      if (!response.ok) throw response;
      if (response.data) {
        setSelectedOrganization(response.data);
      }
    } catch (e) {
      console.error("Error fetching organization:", e);
      toast.error(e.code);
    }
  };

  return (
    <div className="p-6">
      {/* Table Controls */}
      <div className="flex items-center justify-between mb-6">
        <SearchInput
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          className="w-64"
        />
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            {t("total")}: <span className="font-semibold text-gray-900">{total}</span>
          </div>
          <Pagination total={total} limit={limit} currentPage={currentPage} onPageChange={setCurrentPage} />
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t("no_users")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3">{t("table.name")}</th>
                  <th className="px-4 py-3">{t("table.email")}</th>
                  <th className="px-4 py-3">{t("table.role")}</th>
                  <th className="px-4 py-3">{t("table.organization")}</th>
                  <th className="px-4 py-3">{t("table.created_date")}</th>
                  <th className="px-4 py-3">{t("table.last_login")}</th>
                  <th className="px-4 py-3">{t("table.actions")}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => {
                  const userId = user._id || user.id;
                  return (
                    <tr key={userId} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{user.firstname || user.lastname ? `${user.firstname || ""} ${user.lastname || ""}`.trim() : "-"}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">{user.email || "-"}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex flex-wrap gap-1">
                          {user.role_admin && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">{t("roles.admin")}</span>}
                          {user.role_recruiter && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">{t("roles.recruiter")}</span>
                          )}
                          {user.role_candidate && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">{t("roles.candidate")}</span>
                          )}
                          {user.role_agency && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">{t("roles.agency")}</span>
                          )}
                          {!user.role_admin && !user.role_recruiter && !user.role_candidate && !user.role_agency && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">{t("roles.na")}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{user.organisations?.[0]?.name || "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{user.created_at ? new Date(user.created_at).toLocaleDateString("fr-FR") : "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{user.last_login_at ? new Date(user.last_login_at).toLocaleString("fr-FR") : "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewUser(user)}
                            className="px-3 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors cursor-pointer"
                          >
                            {t("buttons.user")}
                          </button>
                          {user.organisations?.length > 0 && (
                            <button
                              onClick={() => handleViewOrganization(user)}
                              className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors cursor-pointer"
                            >
                              {t("buttons.org")}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <UserModal isOpen={!!selectedUser} onClose={() => setSelectedUser(null)} user={selectedUser} fetchData={fetchUsers} />
      <OrganizationModal isOpen={!!selectedOrganization} onClose={() => setSelectedOrganization(null)} organization={selectedOrganization} fetchData={fetchUsers} />
    </div>
  );
};

export default Users;
